import { useState, useRef, useEffect, useCallback } from 'react';
import type { FloatingPosition, FloatingAlignment } from './useFloatingPosition';

export interface TooltipConfig {
  content: string | React.ReactNode;
  position?: FloatingPosition;
  alignment?: FloatingAlignment;
  showDelay?: number;
  hideDelay?: number;
  disabled?: boolean;
}

export interface UseTooltipOptions extends Omit<TooltipConfig, 'content'> {
  // Additional options can be added here
}

export interface TooltipState {
  isVisible: boolean;
  content: string | React.ReactNode;
  position: FloatingPosition;
  alignment: FloatingAlignment;
  triggerRect: DOMRect | null;
}

/**
 * Check if the device's primary input supports hover
 * Uses CSS media query which correctly handles hybrid devices (Surface, etc.)
 * that have both touch and mouse - those will still get tooltips
 */
const canHover = () => {
  if (typeof window === 'undefined') return true;
  // matchMedia may not exist in test environments (jsdom)
  if (typeof window.matchMedia !== 'function') return true;
  // (hover: none) means primary input doesn't support hover (pure touch)
  // (hover: hover) means primary input supports hover (mouse/trackpad)
  return !window.matchMedia('(hover: none)').matches;
};

/**
 * Hook for adding tooltip functionality to any element
 * Returns props to spread on the trigger element
 * Note: Tooltips are disabled when the device's primary input doesn't support hover
 */
export const useTooltip = (content: string | TooltipConfig, options?: UseTooltipOptions) => {
  const [isVisible, setIsVisible] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const triggerRef = useRef<HTMLElement>(null);
  const showTimeoutRef = useRef<NodeJS.Timeout>();
  const hideTimeoutRef = useRef<NodeJS.Timeout>();

  // Parse config
  const config: TooltipConfig = typeof content === 'string' ? { content, ...options } : { ...content, ...options };

  const {
    content: tooltipContent,
    position = 'top',
    alignment = 'center',
    showDelay = 300,
    hideDelay = 100,
    disabled: disabledProp = false,
  } = config;

  // Disable tooltips when primary input doesn't support hover
  const disabled = disabledProp || !canHover();

  // Clear timeouts on unmount
  useEffect(() => {
    return () => {
      if (showTimeoutRef.current) clearTimeout(showTimeoutRef.current);
      if (hideTimeoutRef.current) clearTimeout(hideTimeoutRef.current);
    };
  }, []);

  const handleMouseEnter = useCallback(() => {
    if (disabled) return;

    // Clear any pending hide
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }

    // Show after delay
    showTimeoutRef.current = setTimeout(() => {
      if (triggerRef.current) {
        setTriggerRect(triggerRef.current.getBoundingClientRect());
        setIsVisible(true);
      }
    }, showDelay);
  }, [disabled, showDelay]);

  const handleMouseLeave = useCallback(() => {
    // Clear any pending show
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }

    // Hide after delay
    hideTimeoutRef.current = setTimeout(() => {
      setIsVisible(false);
      setTriggerRect(null);
    }, hideDelay);
  }, [hideDelay]);

  const handleFocus = useCallback(() => {
    if (disabled) return;
    if (triggerRef.current) {
      setTriggerRect(triggerRef.current.getBoundingClientRect());
      setIsVisible(true);
    }
  }, [disabled]);

  const handleBlur = useCallback(() => {
    setIsVisible(false);
    setTriggerRect(null);
  }, []);

  const handleClick = useCallback(() => {
    // Clear any pending show/hide timeouts
    if (showTimeoutRef.current) {
      clearTimeout(showTimeoutRef.current);
      showTimeoutRef.current = undefined;
    }
    if (hideTimeoutRef.current) {
      clearTimeout(hideTimeoutRef.current);
      hideTimeoutRef.current = undefined;
    }

    // Hide tooltip immediately on click
    setIsVisible(false);
    setTriggerRect(null);
  }, []);

  // Generate unique ID for accessibility
  const tooltipId = useRef(`tooltip-${Math.random().toString(36).substr(2, 9)}`);

  const tooltipState: TooltipState = {
    isVisible,
    content: tooltipContent,
    position,
    alignment,
    triggerRect,
  };

  return {
    // Props to spread on trigger element
    triggerProps: {
      ref: triggerRef,
      onMouseEnter: handleMouseEnter,
      onMouseLeave: handleMouseLeave,
      onFocus: handleFocus,
      onBlur: handleBlur,
      onClick: handleClick,
      'aria-describedby': isVisible ? tooltipId.current : undefined,
    },
    // Tooltip state for rendering
    tooltipState,
    tooltipId: tooltipId.current,
  };
};
